import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";

import { ApiError, api } from "../../api/client";
import type { Frago } from "../../api/types";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Select } from "../../components/Select";
import { Button, inputClass, textareaClass } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";

/** « Rédiger le FRAGO N°… » : objet (motif), GDH (proposé par le serveur s'il est laissé vide : heure de la DIREX,
 *  sinon 6 h après l'OPO ou le FRAGO précédent), incident de la DIREX à l'origine du FRAGO. */
export function FragoCreateDialog({
  exerciseId,
  opo,
  next,
  onClose,
  onCreated,
}: {
  exerciseId: string;
  opo: number;
  next: number;
  onClose: () => void;
  onCreated: (frago: Frago) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const [subject, setSubject] = useState("");
  const [dtg, setDtg] = useState("");
  const [incident, setIncident] = useState("");
  const direxQuery = useQuery({ queryKey: ["direx", exerciseId, opo], queryFn: () => api.direx(exerciseId, opo) });
  const incidents = (direxQuery.data?.incidents ?? []).filter((i) => i.status === "injecte" || i.status === "traite");
  const create = useMutation({
    mutationFn: () =>
      api.createFrago(exerciseId, { opo, subject: subject.trim(), incident_id: incident || null, date_time: dtg.trim() || null }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["fragos", exerciseId] });
      notify({ text: t("FRAGO N°{n} rédigé", { n: created.number }), detail: t("Paragraphes relevés depuis l'OPO N°{n}.", { n: opo }), tone: "ok" });
      onCreated(created);
    },
    onError: (err) => notify({ text: err instanceof ApiError ? err.message : t("Opération impossible"), tone: "error" }),
  });
  return (
    <Dialog
      title={t("Rédiger le FRAGO N°{n}", { n: next })}
      subtitle={t("Ordre de conduite modifiant l'OPO N°{n} en cours d'exécution", { n: opo })}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button highlight={!create.isPending} onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? <Waiting>{t("Rédaction…")}</Waiting> : t("Rédiger le FRAGO N°{n}", { n: next })}
          </Button>
        </>
      }
    >
      <DialogBody className="text-ui">
        <p className="leading-snug text-ink-soft">
          {t("Le moteur relève ce qui a changé depuis l'OPO N°{n} (carte, LIMA, heures, ennemi, WINGO, matrice) : seuls ces paragraphes reçoivent un texte, les autres s'impriment « Sans changement ».", { n: opo })}
        </p>
        <label className="block space-y-1">
          <span className="font-semibold text-ink">{t("Objet du FRAGO")}</span>
          <textarea className={textareaClass} rows={2} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("ex : Rupture de contact ENI au nord → recueil de l'EEI par l'EDC")} />
        </label>
        <label className="block space-y-1">
          <span className="font-semibold text-ink">{t("GDH de signature")}</span>
          <input className={clsx(inputClass, "font-mono")} value={dtg} onChange={(e) => setDtg(e.target.value)} placeholder={t("Proposé d'après l'horloge de la DIREX")} />
        </label>
        {incidents.length > 0 && (
          <label className="block space-y-1">
            <span className="font-semibold text-ink">{t("Incident de la DIREX à l'origine du FRAGO")}</span>
            <Select value={incident} onChange={(e) => setIncident(e.target.value)} placeholder={t("Aucun")}>
              <option value="">{t("Aucun")}</option>
              {incidents.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.code} — {i.short}
                </option>
              ))}
            </Select>
          </label>
        )}
      </DialogBody>
    </Dialog>
  );
}
